import { FC } from 'react'
import { Flex } from '@radix-ui/themes'
import cn from 'classnames'
import { Maybe, RadixColorType } from 'shared/types'
import { formatAmountLong } from 'shared/utils/format-price'
import { MIN_PNL } from '../bets-table/constants'
import { XyroNumeral } from '../xyro-numeral'
import { DemoCoinIcon } from './demo-coin-icon'

interface Props {
  /** A virtual stake, payout or profit; a loss is negative. */
  value?: Maybe<number>
  /** Green text for a gain and pink for a loss instead of white. */
  isTextColored?: boolean
  /** The coin colour; green for a gain and pink for a loss by default. */
  iconColor?: Maybe<RadixColorType>
  iconSize?: string
  className?: string
}

/**
 * A virtual demo amount with the demo coin: the off-chain counterpart of
 * `TableItemProfit` (same layout and number format) for stakes, payouts and
 * profits that are not money.
 */
export const DemoAmount: FC<Props> = ({
  value,
  isTextColored = false,
  iconColor = null,
  iconSize = '3rem',
  className
}) => {
  const amount = value ?? 0
  const colorByValue = amount >= 0 ? 'green' : 'pink'

  return (
    <Flex
      gap='2'
      align='center'
      height='100%'
      className={className}
    >
      <DemoCoinIcon
        size={iconSize}
        className={`color-${iconColor || colorByValue}`}
      />

      <XyroNumeral
        className={cn({ 'color-white': !isTextColored })}
        color={isTextColored ? colorByValue : 'gray'}
        isWhite={false}
        size={{ initial: '1', sm: '3' }}
        highContrast
      >
        {formatDemoAmount(amount)}
      </XyroNumeral>
    </Flex>
  )
}

/** `TableItemProfit`'s format: `0`, `<0.01`, or up to two decimals. */
export const formatDemoAmount = (value: number) =>
  value === 0 ? '0'
  : Math.abs(value) < MIN_PNL ? '<0.01'
  : formatAmountLong(value)
